import { getMsalAuth } from '../AuthProvider';

interface FeedbackReporter {
  email: string;
  name?: string;
}

interface FeedbackWidgetConfig {
  sourceApp: string;
  environment: string;
  tags: string | string[];
  getReporter: () => FeedbackReporter | Promise<FeedbackReporter>;
  version?: string;
}

interface FeedbackWidgetController {
  open: () => void;
  close: () => void;
  destroy: () => void;
}

interface DaikinFeedbackApi {
  init: (config: FeedbackWidgetConfig) => FeedbackWidgetController;
}

declare global {
  interface Window {
    DaikinFeedback?: DaikinFeedbackApi;
  }
}

let widgetScriptPromise: Promise<DaikinFeedbackApi> | null = null;

export function loadFeedbackWidget(scriptUrl: string): Promise<DaikinFeedbackApi> {
  if (window.DaikinFeedback) return Promise.resolve(window.DaikinFeedback);
  if (widgetScriptPromise) return widgetScriptPromise;

  widgetScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.dataset.daikinFeedbackWidget = 'true';
    script.onload = () => {
      if (window.DaikinFeedback) {
        resolve(window.DaikinFeedback);
        return;
      }

      widgetScriptPromise = null;
      reject(new Error('The feedback widget script did not expose its API.'));
    };
    script.onerror = () => {
      widgetScriptPromise = null;
      script.remove();
      reject(new Error('Unable to load the feedback widget script.'));
    };
    document.head.append(script);
  });

  return widgetScriptPromise;
}

export async function initializeHostedFeedbackWidget(): Promise<FeedbackWidgetController> {
  const scriptUrl = process.env.REACT_APP_FEEDBACK_WIDGET_URL?.trim();
  const environment = process.env.REACT_APP_FEEDBACK_ENVIRONMENT?.trim();
  if (!scriptUrl) throw new Error('Feedback widget URL is not configured.');
  if (!environment) throw new Error('Feedback environment is not configured.');

  const widget = await loadFeedbackWidget(scriptUrl);
  return widget.init({
    sourceApp: process.env.REACT_APP_FEEDBACK_SOURCE_APP || 'CodeSheetApp',
    environment,
    tags: process.env.REACT_APP_FEEDBACK_TAGS || 'CodesheetApp',
    version: process.env.REACT_APP_VERSION || undefined,
    getReporter: () => {
      const account = getMsalAuth().accounts?.[0];
      if (!account?.username) throw new Error('Feedback reporter is not available.');
      return { email: account.username, name: account.name };
    },
  });
}
